import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useConfig } from '../context/ConfigContext';

const Agenda = () => {
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { config } = useConfig();

    useEffect(() => {
        fetchAgenda();
    }, []);

    const fetchAgenda = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/agenda?status=active');
            setEvents(res.data);
        } catch (error) {
            console.error("Error fetching agenda", error);
        } finally {
            setIsLoading(false);
        }
    };

    const pageDesign = config?.pageDesigns?.agenda || {};
    const isImageOnly = pageDesign.agendaImageOnly;
    const primaryColor = config?.primaryColor || '#9d4edd';

    const getFlagEmoji = (country) => {
        if (!country) return '';
        const name = country.trim().toLowerCase();
        const flags = {
            'colombia': '🇨🇴', 'méxico': '🇲🇽', 'mexico': '🇲🇽', 'argentina': '🇦🇷',
            'chile': '🇨🇱', 'perú': '🇵🇪', 'peru': '🇵🇪', 'ecuador': '🇪🇨',
            'españa': '🇪🇸', 'estados unidos': '🇺🇸', 'usa': '🇺🇸', 'brasil': '🇧🇷',
            'brazil': '🇧🇷', 'venezuela': '🇻🇪', 'uruguay': '🇺🇾', 'paraguay': '🇵🇾',
            'bolivia': '🇧🇴', 'panamá': '🇵🇦', 'panama': '🇵🇦', 'costa rica': '🇨🇷',
            'cuba': '🇨🇺', 'república dominicana': '🇩🇴', 'republica dominicana': '🇩🇴',
            'puerto rico': '🇵🇷', 'honduras': '🇭🇳', 'el salvador': '🇸🇻',
            'guatemala': '🇬🇹', 'nicaragua': '🇳🇮', 'italia': '🇮🇹', 'francia': '🇫🇷',
            'alemania': '🇩🇪', 'reino unido': '🇬🇧', 'canadá': '🇨🇦', 'canada': '🇨🇦'
        };
        return flags[name] || '';
    };

    // Agrupar por fecha y luego por "bloque"
    const groupedData = events.reduce((acc, event) => {
        const dateStr = new Date(event.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
        const capitalizedDate = dateStr.charAt(0).toUpperCase() + dateStr.slice(1);

        if (!acc[capitalizedDate]) acc[capitalizedDate] = {};

        const block = event.block_name || 'Sin Bloque';
        if (!acc[capitalizedDate][block]) acc[capitalizedDate][block] = {
            name: block,
            color: event.block_color || '#9d4edd',
            events: []
        };

        // Unificar ponentes si la hora y titulo coinciden
        const existingEvent = acc[capitalizedDate][block].events.find(e => e.title === event.title && e.start_time === event.start_time);

        if (existingEvent) {
            if (event.speaker_name) {
                existingEvent.speakers = existingEvent.speakers || [];
                if (!existingEvent.speakers.some(s => s.name === event.speaker_name)) {
                    existingEvent.speakers.push({ name: event.speaker_name, country: event.speaker_country || event.country });
                }
            } else if (event.country) {
                existingEvent.speakers = existingEvent.speakers || [];
                if (!existingEvent.speakers.some(s => s.country === event.country)) {
                    existingEvent.speakers.push({ name: '', country: event.country });
                }
            }
        } else {
            const newEvent = { ...event, speakers: [] };
            if (event.speaker_name) {
                newEvent.speakers.push({ name: event.speaker_name, country: event.speaker_country || event.country });
            } else if (event.country) {
                newEvent.speakers.push({ name: '', country: event.country });
            }
            acc[capitalizedDate][block].events.push(newEvent);
        }

        return acc;
    }, {});

    const getIcon = (type) => {
        if (type === 'coffee') return '☕';
        if (type === 'lunch') return '🍴';
        return null;
    };

    if (isImageOnly) {
        return (
            <div className="animate-fadeIn min-h-screen flex flex-col items-center p-0 bg-transparent">
                <img
                    src={pageDesign.staticImage || "https://ftp.pcnpublicidad.com/neuroupdate/resources/Agenda_4toNeuroUpdate.png"}
                    alt="Agenda 4to NeuroUpdate"
                    className="w-full md:w-[90vw] h-auto shadow-2xl mb-10"
                />
            </div>
        );
    }

    const showTitle = pageDesign.showTitle !== false;
    const pageTitle = pageDesign.title || 'Agenda';
    const pageSubtitle = pageDesign.subtitle || 'Programa Académico';

    return (
        <div className="animate-fadeIn max-w-5xl mx-auto pb-32 font-['Alliance-Light']">
            {showTitle && (
                <header className="mb-12 text-center pt-8">
                    <h1 className="text-4xl sm:text-5xl font-black text-white uppercase tracking-tighter italic">{pageTitle}</h1>
                    <p className="text-white/80 text-[10px] font-black uppercase tracking-[0.4em] mt-2">{pageSubtitle}</p>
                </header>
            )}

            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-12 h-12 border-4 border-white/10 border-t-purple-500 rounded-full animate-spin"></div>
                    <p className="text-white/50 text-[10px] font-black uppercase tracking-[0.4em] mt-6 animate-pulse">Cargando Agenda...</p>
                </div>
            ) : (
                <div className="space-y-16">
                    {Object.keys(groupedData).map((date, dayIdx) => (
                        <div key={dayIdx} className="space-y-8">
                            {/* Cabecera de Día */}
                            <div className="flex items-center gap-4 bg-gradient-to-r from-purple-800 to-indigo-900 p-6 rounded-[30px] border border-white/20 shadow-xl">
                                <div className="bg-white/10 w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-inner">🗓️</div>
                                <h2 className="text-xl sm:text-2xl font-black text-white uppercase italic tracking-tight">{date}</h2>
                            </div>

                            {/* Bloques del Día */}
                            <div className="space-y-10">
                                {Object.values(groupedData[date]).map((block, blockIdx) => (
                                    <div
                                        key={blockIdx}
                                        className="relative rounded-[40px] border-2 overflow-hidden transition-all duration-500 hover:shadow-[0_0_30px_rgba(255,255,255,0.1)]"
                                        style={{ borderColor: `${block.color}77`, backgroundColor: 'rgba(255,255,255,0.03)' }}
                                    >
                                        {/* Header del Bloque */}
                                        {block.name !== 'Sin Bloque' && (
                                            <div
                                                className="px-8 py-4 font-black uppercase text-[11px] tracking-[0.2em] italic flex justify-between items-center"
                                                style={{ backgroundColor: `${block.color}44`, color: '#FFFFFF' }}
                                            >
                                                <span>{block.name}</span>
                                                <div className="flex gap-1">
                                                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#FFFFFF' }}></div>
                                                    <div className="w-1.5 h-1.5 rounded-full opacity-50" style={{ backgroundColor: '#FFFFFF' }}></div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="p-4 sm:p-8 space-y-0 divide-y divide-white/10">
                                            {block.events.map((event, i) => {
                                                const icon = getIcon(event.activity_type);
                                                const isSpecial = event.activity_type !== 'session';

                                                return (
                                                    <div key={i} className={`flex flex-col sm:flex-row py-8 gap-6 group transition-all ${isSpecial ? 'bg-white/10 mx-[-2rem] px-[2rem]' : ''}`}>
                                                        {/* Hora */}
                                                        <div className="w-24 shrink-0">
                                                            <div className="flex flex-col text-[13px] font-black text-white/70 tracking-widest leading-none group-hover:text-white transition-colors">
                                                                <span>{event.start_time.slice(0, 5)}</span>
                                                                <div className="h-4 w-[1px] bg-white/20 my-1 ml-2"></div>
                                                                <span>{event.end_time.slice(0, 5)}</span>
                                                            </div>
                                                        </div>

                                                        {/* Icono (opcional) */}
                                                        {icon && (
                                                            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-white/10 text-2xl shadow-xl border border-white/20">
                                                                {icon}
                                                            </div>
                                                        )}

                                                        {/* Tema */}
                                                        <div className="flex-grow">
                                                            <h3 className={`text-[16px] sm:text-[18px] font-black leading-tight uppercase italic tracking-tight ${isSpecial ? 'text-white' : 'text-yellow-400'}`}>
                                                                {event.title}
                                                            </h3>
                                                            {event.moderator && (
                                                                <p className="text-[10px] text-white/70 font-bold mt-2 uppercase tracking-widest">Moderador: {event.moderator}</p>
                                                            )}
                                                        </div>

                                                        {/* Conferencistas */}
                                                        <div className="w-full sm:w-48 shrink-0">
                                                            <div className="flex flex-col gap-3">
                                                                {event.speakers.map((speaker, idx) => (
                                                                    <div key={idx} className="flex items-center justify-end gap-3 text-right">
                                                                        <span className="text-[12px] font-black text-white uppercase tracking-tighter group-hover:text-yellow-400">{speaker.name}</span>
                                                                        {speaker.country && (
                                                                            <span className="text-xl filter drop-shadow-md" title={speaker.country}>
                                                                                {getFlagEmoji(speaker.country)}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

        </div>
    );
};

export default Agenda;
